import Image from '@/components/Image';
import { MAX_CONTAINER_WIDTH } from '@/components/layout/Container';
import Link from '@/components/Link';
import { PRIMARY_NAV_HEIGHT } from '@/components/navigation/PrimaryNavigation';
import VizyTextPage from '@/components/vizy/VizyTextPage';
import { MultiChapterGuideQuery } from '@/graphql';
import { getSingle } from '@/lib/typescript-helpers';
import { COLORS } from '@/styles/color';
import { getBrandColor, hexToRGBA } from '@/styles/color/utils';
import {
  applyBreakpointPadding,
  marginX,
  mediaBreakpointDown,
  mediaBreakpointUp,
  paddingX,
  paddingY,
} from '@/styles/layout/utils';
import { FONT_WEIGHTS } from '@/styles/typography';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { kebabCase } from 'lodash';
import { useState } from 'react';
import { InView } from 'react-intersection-observer';
import styled from 'styled-components';

const PageWrapper = styled.div`
  max-width: calc(${remCalc(MAX_CONTAINER_WIDTH)} + var(--breakpoint-padding-x));
  ${marginX('auto')}
  margin-top: ${remCalc(PRIMARY_NAV_HEIGHT)};
`;

interface HeaderProps {
  backgroundColor: string;
}

const Header = styled.header<HeaderProps>`
  position: relative;
  z-index: 1;

  ${applyBreakpointPadding};
  background-color: ${(props) => props.backgroundColor};
  padding-top: ${remCalc(30)};

  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  ${mediaBreakpointUp('tablet')} {
    padding-top: ${remCalc(72)};
  }

  ${mediaBreakpointUp('desktop')} {
    border-radius: ${remCalc(10)};
  }
`;

const HeaderImage = styled(Image)`
  width: 100%;
  max-width: ${remCalc(618)};
`;

const MainContent = styled.article`
  position: relative;
  z-index: 2;

  border-radius: ${remCalc(10, 10, 0, 0)};

  margin-top: ${remCalc(-50)};
  min-height: ${remCalc(50)};

  padding-top: ${remCalc(24)};
  padding-right: calc(var(--breakpoint-padding-x) / 2);
  padding-bottom: ${remCalc(76)};
  padding-left: calc(var(--breakpoint-padding-x) / 2);

  margin-left: calc(var(--breakpoint-padding-x) / 2);
  margin-right: calc(var(--breakpoint-padding-x) / 2);

  background-color: ${COLORS.WHITE};

  display: grid;
  grid-template-columns: auto;
  grid-template-rows: auto auto auto;
  grid-row-gap: ${remCalc(40)};

  ${mediaBreakpointUp('tablet')} {
    padding-left: calc(var(--breakpoint-padding-x) / 2);
    padding-right: calc(var(--breakpoint-padding-x) / 2);
  }

  ${mediaBreakpointUp('desktop')} {
    padding-top: calc(var(--breakpoint-padding-x) / 2);
    grid-template-columns: ${remCalc(250)} auto;
    grid-template-rows: auto minmax(0, 1fr);
    grid-column-gap: ${remCalc(60)};
    grid-row-gap: 0;
  }

  ${mediaBreakpointUp('desktopWide')} {
    grid-template-columns: ${remCalc(300)} auto;
  }
`;

const Introduction = styled.div`
  ${mediaBreakpointUp('desktop')} {
    grid-row: 1;
    grid-column: 2;
  }

  h1 {
    ${customTextSize(40, 42)}
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(40)};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(72, 70)}
    }
  }

  > p:first-of-type {
    ${customTextSize(22, 35)};
  }
`;

const ChapterListColumn = styled.aside`
  ${mediaBreakpointUp('desktop')} {
    grid-row: 1 / span 2;
    grid-column: 1;
  }
`;

const ChapterListWrapper = styled.nav`
  position: sticky;
  top: ${remCalc(PRIMARY_NAV_HEIGHT + 50)};

  background-color: ${COLORS.GRAY.HIGHLIGHT};
  border-radius: ${remCalc(10)};
  min-height: ${remCalc(250)};

  padding: ${remCalc(31, 8, 8)};

  ${mediaBreakpointDown('tablet')} {
    margin-left: calc(var(--breakpoint-padding-x) / -2);
    margin-right: calc(var(--breakpoint-padding-x) / -2);
  }
`;

const ChapterListHeading = styled.div`
  ${customTextSize(24, 30)}
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  ${paddingX(24)}
  margin-bottom: ${remCalc(8)};
`;

const ChapterList = styled.ol``;

interface ChapterListItemProps {
  active: boolean;
}

const ChapterListItem = styled.li<ChapterListItemProps>`
  position: relative;

  a {
    display: block;
    ${paddingX(24)}
    ${paddingY(8)}

    border-radius: ${remCalc(10)};
    background-color: transparent;

    transition: background-color 0.15s ease-in-out;

    &:hover {
      background-color: ${hexToRGBA(COLORS.GRAY.BASE, 0.25)};
    }

    span {
      display: block;

      strong {
        color: ${(props) => (props.active ? COLORS.BLUE.BASE : COLORS.BLACK)};
      }
    }
  }

  &::before {
    content: '';
    width: ${remCalc(6)};
    height: ${remCalc(6)};
    position: absolute;
    top: ${remCalc(18)};
    left: ${remCalc(6)};
    background-color: ${(props) => (props.active ? COLORS.BLUE.BASE : 'transparent')};
    border-radius: 100%;
    transition: background-color 0.15s ease-in-out;
  }
`;

const Content = styled.div`
  ${mediaBreakpointUp('desktop')} {
    grid-row: 2;
    grid-column: 2;
  }
`;

const ChapterWrapper = styled.div`
  margin-top: ${remCalc(40)};

  ${mediaBreakpointUp('desktop')} {
    margin-top: ${remCalc(100)};
  }

  h2 {
    ${customTextSize(32, 38)};
    font-weight: ${FONT_WEIGHTS.SEMIBOLD};
    margin-bottom: ${remCalc(26)};

    ${mediaBreakpointUp('desktop')} {
      ${customTextSize(49, 58)};
      margin-bottom: ${remCalc(40)};
    }

    small {
      display: block;
      ${customTextSize(16, 24)};

      ${mediaBreakpointUp('desktop')} {
        ${customTextSize(18, 26)};
      }

      span {
        visibility: hidden;
        position: absolute;
        top: -9999px;
        left: -9999px;
      }
    }
  }

  > p:first-of-type {
    ${customTextSize(22, 35)};
  }
`;

const MultiChapterGuidePage: PageTemplate<MultiChapterGuideQuery> = ({ entry }) => {
  const heroImage = getSingle(entry?.coverImage);

  const [activeChapters, setActiveChapters] = useState<number[]>([]);

  return (
    <PageWrapper>
      <Header backgroundColor={getBrandColor(entry?.brandColor?.label!, 'tint') ?? COLORS.YELLOW.TINT}>
        {heroImage && (
          <HeaderImage
            src={heroImage.url!}
            width={heroImage.width!}
            height={heroImage.height!}
            alt={heroImage.altText!}
          />
        )}
      </Header>

      <MainContent>
        <Introduction>
          <h1>{entry?.title}</h1>

          {!!entry?.description?.rawNodes && <VizyTextPage rawNodes={entry?.description?.rawNodes} nodes={[]} />}
        </Introduction>

        <ChapterListColumn>
          <ChapterListWrapper>
            <ChapterListHeading>Chapters</ChapterListHeading>

            <ChapterList>
              {entry?.guideChapters?.map((chapter, index) => (
                <ChapterListItem key={chapter?.id} active={Math.max(...activeChapters) === index}>
                  <Link href={`#${kebabCase(chapter?.title ?? '')}`} shallow>
                    <a
                      onClick={(e) => {
                        e.preventDefault();

                        const el = document.getElementById(kebabCase(chapter?.title ?? ''));

                        if (!el) return;

                        const rect = el.getBoundingClientRect();

                        window.scrollTo({
                          top: rect.top + window.pageYOffset - PRIMARY_NAV_HEIGHT - 50,
                          behavior: 'smooth',
                        });
                      }}
                    >
                      <span>
                        <strong>{chapter?.title}</strong>
                      </span>
                      <span>{chapter?.name}</span>
                    </a>
                  </Link>
                </ChapterListItem>
              ))}
            </ChapterList>
          </ChapterListWrapper>
        </ChapterListColumn>

        <Content>
          {entry?.guideChapters?.map((chapter, index) => (
            <InView
              as={ChapterWrapper}
              id={kebabCase(chapter?.title ?? '')}
              key={chapter?.id}
              onChange={(inView) => {
                if (inView) {
                  setActiveChapters([...new Set([...activeChapters, index])]);
                } else {
                  setActiveChapters(activeChapters.filter((chapter) => chapter !== index));
                }
              }}
            >
              <h2>
                <small>
                  {chapter?.title}
                  <span>:&nbsp;</span>
                </small>
                {chapter?.name}
              </h2>

              <VizyTextPage rawNodes={chapter?.content?.rawNodes} nodes={[]} />
            </InView>
          ))}
        </Content>
      </MainContent>
    </PageWrapper>
  );
};

export default MultiChapterGuidePage;
